import { copyToClipboard } from '@mi-front/core';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Button, ButtonProps, IconButton, IconButtonProps } from '@mui/material';
import { ReactNode, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

export interface CopyBtnProps {
  copyValue: string;
  children?: ReactNode;
  mobile?: boolean;
  onCopied?: () => void;
  size?: ButtonProps['size'] | IconButtonProps['size'];
  variant?: ButtonProps['variant'];
  color?: ButtonProps['color'];
  sx?: ButtonProps['sx'];
  disabled?: boolean;
}

export function CopyBtn(p: CopyBtnProps) {
  const {
    copyValue,
    children,
    mobile,
    onCopied,
    size = 'small',
    variant = 'outlined',
    color = 'secondary',
    sx,
    disabled = false,
  } = p;
  const { $t } = useIntl();

  let textChangeTimeout: number = null!;
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    return () => {
      if (textChangeTimeout) {
        clearTimeout(textChangeTimeout);
      }
    };
  }, [textChangeTimeout]);

  const handleClick = () => {
    setCopied(true);
    copyToClipboard(copyValue);
    onCopied && onCopied();

    textChangeTimeout = window.setTimeout(() => {
      setCopied(false);
    }, 3000);
  };

  if (mobile) {
    return (
      <IconButton
        size={size as IconButtonProps['size']}
        onClick={handleClick}
        disabled={copied || disabled}
        color={color as IconButtonProps['color']}
        sx={sx}
      >
        {copied ? <CheckIcon /> : <ContentCopyIcon />}
      </IconButton>
    );
  }

  return (
    <Button
      variant={variant}
      size={size}
      color={color}
      onClick={handleClick}
      disabled={copied || disabled}
      startIcon={copied ? <CheckIcon /> : <ContentCopyIcon />}
      sx={{ minWidth: '8rem', ...sx }}
    >
      {copied ? $t({ id: 'common.copyBtn.copied', defaultMessage: 'Copied' }) : children}
    </Button>
  );
}
